import type { Staff } from './types';

/**
 * The signed-in staff session. The refresh token is kept in localStorage so a reload stays signed in; every tab
 * shares it (refresh tokens rotate on each use, so tabs must never refresh with a stale copy — see api.ts).
 */
export interface Session {
  accessToken: string;
  refreshToken: string;
  admin: Staff;
}

const KEY = 'zr.admin.session';
type Listener = (s: Session | null) => void;
const listeners = new Set<Listener>();

function readStored(): Session | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const s = JSON.parse(raw) as Session;
    return s && s.accessToken && s.refreshToken && s.admin ? s : null;
  } catch {
    return null;
  }
}

let current: Session | null = readStored();

function emit() {
  for (const l of listeners) l(current);
}

export const session = {
  get: () => current,
  /** Re-read from storage (another tab may have rotated the tokens). */
  reload(): Session | null {
    current = readStored();
    return current;
  },
  set(next: Session | null) {
    current = next;
    try {
      if (next) localStorage.setItem(KEY, JSON.stringify(next));
      else localStorage.removeItem(KEY);
    } catch {
      /* private mode: the session lives in memory only */
    }
    emit();
  },
  subscribe(l: Listener) {
    listeners.add(l);
    return () => {
      listeners.delete(l);
    };
  },
};

if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => {
    if (e.key !== KEY) return;
    const before = current;
    current = readStored();
    if (before?.accessToken !== current?.accessToken || !!before !== !!current) emit();
  });
}
